import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  idToken: string | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Decode JWT to check for admin claim
  const checkAdminClaim = (token: string): boolean => {
    try {
      // JWT has 3 parts: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode the payload (base64url)
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      );

      return payload.admin === true;
    } catch (err) {
      console.error('Error decoding token:', err);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      setError(null);

      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken(true);
          const adminStatus = checkAdminClaim(token);

          if (!adminStatus) {
            setError('Access denied. Admin privileges required.');
            await firebaseSignOut(auth);
            setUser(null);
            setIdToken(null);
            setIsAdmin(false);
          } else {
            setUser(firebaseUser);
            setIdToken(token);
            setIsAdmin(true);
            setError(null);
          }
        } catch (err) {
          console.error('Error getting ID token:', err);
          setError('Failed to verify admin status.');
          setUser(null);
          setIdToken(null);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIdToken(null);
        setIsAdmin(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Refresh token periodically (every 50 minutes, tokens expire after 1 hour)
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(
      async () => {
        try {
          const token = await user.getIdToken(true);
          const adminStatus = checkAdminClaim(token);

          if (!adminStatus) {
            setError('Admin privileges revoked.');
            await firebaseSignOut(auth);
            setUser(null);
            setIdToken(null);
            setIsAdmin(false);
          } else {
            setIdToken(token);
          }
        } catch (err) {
          console.error('Error refreshing token:', err);
        }
      },
      50 * 60 * 1000
    );

    return () => clearInterval(refreshInterval);
  }, [user]);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const token = await userCredential.user.getIdToken(true);
      const adminStatus = checkAdminClaim(token);

      if (!adminStatus) {
        setError('Access denied. Admin privileges required.');
        await firebaseSignOut(auth);
        throw new Error('Not an admin user');
      }

      // State will be updated by onAuthStateChanged
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const token = await userCredential.user.getIdToken(true);
      const adminStatus = checkAdminClaim(token);

      if (!adminStatus) {
        setError('Access denied. Admin privileges required.');
        await firebaseSignOut(auth);
        throw new Error('Not an admin user');
      }

      // State will be updated by onAuthStateChanged
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in with Google';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);

    try {
      await firebaseSignOut(auth);
      setUser(null);
      setIdToken(null);
      setIsAdmin(false);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign out';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    idToken,
    isAdmin,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
